import { Controller } from "@hotwired/stimulus"
import consumer from "channels/consumer"
import { logger } from "../logger"

// Handles real-time ZIP export progress updates via ActionCable.
// Follows the same pattern as report_controller.js with sessionStorage persistence.
export default class extends Controller {
  static targets = ["progressBar", "message", "close"]
  static values = {
    cacheId: Number
  }

  connect() {
    this.isClosing = false

    if (this.hasCacheIdValue && !this.subscription) {
      // Store in sessionStorage for restoration after navigation
      this.addToSessionStorage()

      this.subscription = consumer.subscriptions.create(
        {
          channel: "ZipExportChannel",
          zip_export_cache_id: this.cacheIdValue
        },
        {
          connected: this.connected.bind(this),
          disconnected: this.disconnected.bind(this),
          received: this.received.bind(this)
        }
      )
    }
  }

  disconnect() {
    if (this.subscription && !this.manualCloseInProgress) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  connected() {
    this.subscription.perform('check_status')
  }

  disconnected() {
  }

  received(data) {
    logger.log('ZipExportChannel received:', data)

    if (this.isClosing) return

    if (data.status === 'progress') {
      this.updateProgress(data)
    } else if (data.status === 'done') {
      this.handleDone(data)
    } else if (data.status === 'failed') {
      this.handleFailed(data)
    } else if (data.status === 'start') {
      if (this.hasMessageTarget && data.message) {
        this.messageTarget.textContent = data.message
      }
    }
  }

  updateProgress(data) {
    if (this.hasProgressBarTarget) {
      const percent = Math.round((data.current / data.total) * 100)
      this.progressBarTarget.style.width = `${percent}%`
      this.progressBarTarget.setAttribute('aria-valuenow', percent)
    }
    if (this.hasMessageTarget && data.message) {
      this.messageTarget.textContent = data.message
    }
  }

  handleDone(data) {
    if (data.download_initiated) {
      logger.log('ZipExport: Download already initiated')
      this.isClosing = true
      this.closeStatus()
      return
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '100%'
      this.progressBarTarget.setAttribute('aria-valuenow', 100)
      this.progressBarTarget.classList.remove('progress-bar-animated', 'progress-bar-striped')
      this.progressBarTarget.classList.add('bg-success')
    }
    if (this.hasMessageTarget && data.message) {
      this.messageTarget.textContent = data.message
    }

    if (data.url) {
      this.initiateDownload(data.url)
    }
  }

  handleFailed(data) {
    this.isClosing = true

    if (this.hasProgressBarTarget) {
      const progressContainer = this.progressBarTarget.closest('.progress')
      if (progressContainer) progressContainer.remove()
    }

    if (this.hasMessageTarget && data.message) {
      this.messageTarget.innerHTML = `<span class="text-warning"><b>${data.message}</b></span>`
    }

    setTimeout(() => {
      this.serverClose()
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      this.closeStatus()
    }, 5000)
  }

  async initiateDownload(downloadUrl) {
    try {
      const response = await fetch(`/zip_export_caches/${this.cacheIdValue}/initiate_download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.isClosing = true
        this.startDownload(downloadUrl)
      } else {
        logger.log('ZipExport: Download already initiated by another tab')
        this.isClosing = true
        this.closeStatus()
      }
    } catch (error) {
      logger.log('ZipExport: Error initiating download:', error)
      this.isClosing = true
      this.startDownload(downloadUrl)
    }
  }

  async startDownload(downloadUrl) {
    logger.log('ZipExport: Starting download via fetch:', downloadUrl)

    try {
      const response = await fetch(downloadUrl, {
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = 'download.zip'
      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1])
        } else {
          const match = contentDisposition.match(/filename="?([^";\n]+)"?/i)
          if (match) {
            filename = match[1]
          }
        }
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        link.remove()
        window.URL.revokeObjectURL(url)
      }, 100)
    } catch (error) {
      logger.log('ZipExport: Download error:', error)
    }

    this.closeStatus()
  }

  close(event) {
    event.preventDefault()
    if (this.subscription) {
      this.manualCloseInProgress = true
      this.serverClose()
      setTimeout(() => {
        if (this.subscription) {
          this.subscription.unsubscribe()
          this.subscription = null
        }
        this.closeStatus()
      }, 100)
    } else {
      this.closeStatus()
    }
  }

  serverClose() {
    fetch(`/zip_export_caches/${this.cacheIdValue}/close_export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    }).catch(error => {
      logger.log('ZipExport: Close request failed:', error)
    })
  }

  closeStatus() {
    this.removeFromSessionStorage()

    const container = document.getElementById('reportStatuses')

    this.element.remove()

    requestAnimationFrame(() => {
      if (container) {
        const remainingStatuses = container.querySelectorAll('.report-status').length
        if (remainingStatuses === 0) {
          container.remove()
          document.documentElement.style.setProperty('--report-status-overlay-height', '0px')
        } else if (window.updateReportStatusOverlayHeight) {
          window.updateReportStatusOverlayHeight()
        }
      }
    })
  }

  addToSessionStorage() {
    try {
      let active = JSON.parse(sessionStorage.getItem('activeZipExports') || '[]')
      if (!active.includes(this.cacheIdValue)) {
        active.push(this.cacheIdValue)
        sessionStorage.setItem('activeZipExports', JSON.stringify(active))
      }
    } catch (error) {
      logger.log('ZipExport: Error saving to sessionStorage:', error)
    }
  }

  removeFromSessionStorage() {
    try {
      let active = JSON.parse(sessionStorage.getItem('activeZipExports') || '[]')
      active = active.filter(id => id !== this.cacheIdValue)
      sessionStorage.setItem('activeZipExports', JSON.stringify(active))
    } catch (error) {
      logger.log('ZipExport: Error removing from sessionStorage:', error)
    }
  }
}
